import {
  EncoderError,
  type EncoderConnectionStatus,
  type EncoderDeviceInfo,
  type EncoderFailureCode,
  type EncoderFrameLogEntry,
  type UhfEncoderClient,
} from './uhf-encoder-client'

/**
 * THY / SW desktop UHF encoder over the vendor's local bridge.
 *
 * Protocol constraints proven on the hardware:
 *  - the bridge greets with the plain text "Welcome to RFID Server" (not JSON)
 *  - `Msg: 2` frames arrive unsolicited
 *  - there is NO correlation id, so replies match by order only
 *  - reply `Val` is a SCALAR (number for params, hex string for card data),
 *    never an array — confirmed by live capture
 *  - `Msg: 1` is the only success code for every op we use
 *
 * Everything therefore runs strictly one request at a time. A timed-out request
 * is drained rather than abandoned: without that, a late reply would be handed
 * to the next request and a failed write could be reported as a success.
 */

const BRIDGE_URL = 'ws://127.0.0.1:1818'
const REQUEST_TIMEOUT_MS = 4000
const CONNECT_TIMEOUT_MS = 5000
const POST_OPEN_SETTLE_MS = 150
const FRAME_LOG_LIMIT = 200
const TID_WORDS = 6
const PARAM_WORK_MODE = 2
const PARAM_RF_POWER = 5
const WORK_MODE_ANSWER = 0
const MEMORY_BANK_EPC = 1
const MEMORY_BANK_TID = 2
const EPC_PTR_PC_WORD = 1
const EPC_PTR_EPC_DATA = 2
const TID_PTR = 0
const DEFAULT_ACCESS_PASSWORD = '00000000'
const ANY_ANTENNA = 255
const MSG_OK = 1

interface BridgeFrame {
  Msg: number
  Val?: unknown
}

interface PendingRequest {
  resolve: (frame: BridgeFrame) => void
  reject: (error: Error) => void
  timer: ReturnType<typeof setTimeout>
}

function isBridgeFrame(value: unknown): value is BridgeFrame {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { Msg?: unknown }).Msg === 'number'
  )
}

/**
 * Hex payload from a bridge reply. Val is a scalar string on this device
 * ("3000", "E280…"); arrays of byte numbers are kept as a defensive fallback.
 */
function hexFromFrame(frame: BridgeFrame, expectedChars?: number): string | null {
  const value = frame.Val

  if (typeof value === 'string') {
    const trimmed = value.trim().toUpperCase()
    if (!/^[0-9A-F]+$/.test(trimmed)) return null
    if (expectedChars !== undefined && trimmed.length !== expectedChars) return null
    return trimmed
  }

  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    const hex = value.toString(16).toUpperCase()
    const padded =
      expectedChars !== undefined ? hex.padStart(expectedChars, '0') : hex
    if (expectedChars !== undefined && padded.length !== expectedChars) return null
    return padded
  }

  if (Array.isArray(value)) {
    const bytes = value.filter((n): n is number => typeof n === 'number')
    if (bytes.length === 0) return null
    const hex = bytes.map((n) => (n & 0xff).toString(16).toUpperCase().padStart(2, '0')).join('')
    if (expectedChars !== undefined && hex.length !== expectedChars) return null
    return hex
  }

  return null
}

/** Numeric param from a bridge reply. Val is a number (0, 17) on this device. */
function numberFromFrame(frame: BridgeFrame): number | null {
  const value = frame.Val
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim()))
    return Number.parseInt(value.trim(), 10)
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (typeof entry === 'number' && Number.isFinite(entry)) return entry
      if (typeof entry === 'string' && /^-?\d+$/.test(entry.trim()))
        return Number.parseInt(entry.trim(), 10)
    }
  }
  return null
}

/**
 * Decode GetDeviceSystemInfo Val "5D16D3042607215144":
 *   5D -> firmware 5.13, 16 -> hardware 1.6, remainder -> serial.
 */
function decodeDeviceInfoString(raw: string): {
  serial: string | null
  firmware: string | null
  hardware: string | null
} {
  const value = raw.trim().toUpperCase()
  if (!/^[0-9A-F]{6,}$/.test(value)) {
    return { serial: value || null, firmware: null, hardware: null }
  }

  const fwByte = Number.parseInt(value.slice(0, 2), 16)
  const hwByte = Number.parseInt(value.slice(2, 4), 16)
  const serial = value.slice(4)

  return {
    serial: serial || null,
    firmware: `${(fwByte >> 4) & 0xf}.${fwByte & 0xf}`,
    hardware: `${(hwByte >> 4) & 0xf}.${hwByte & 0xf}`,
  }
}

function assertOk(
  frame: BridgeFrame,
  ops: string,
  code: EncoderFailureCode,
  message: string
): void {
  if (frame.Msg === MSG_OK) return
  throw new EncoderError(code, `${message} (Msg=${frame.Msg}, ops=${ops})`)
}

export class ThyWebSocketEncoderClient implements UhfEncoderClient {
  private socket: WebSocket | null = null
  private status: EncoderConnectionStatus = 'disconnected'
  private deviceInfo: EncoderDeviceInfo | null = null
  private pending: PendingRequest | null = null
  private queue: Promise<unknown> = Promise.resolve()
  private consecutiveTimeouts = 0
  private readonly listeners = new Set<(status: EncoderConnectionStatus) => void>()
  private readonly frameLog: EncoderFrameLogEntry[] = []

  getStatus(): EncoderConnectionStatus {
    return this.status
  }

  getDeviceInfo(): EncoderDeviceInfo | null {
    return this.deviceInfo
  }

  getFrameLog(): EncoderFrameLogEntry[] {
    return [...this.frameLog]
  }

  onStatusChange(listener: (status: EncoderConnectionStatus) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  async connect(): Promise<EncoderDeviceInfo> {
    if (this.status === 'connected' && this.deviceInfo) return this.deviceInfo

    await this.openSocket()
    // Let the greeting land and be discarded before the first request.
    await new Promise((resolve) => setTimeout(resolve, POST_OPEN_SETTLE_MS))

    const openResult = await this.request('OpenUSB', [0])
    if (openResult.Msg !== MSG_OK) {
      await this.disconnect()
      throw new EncoderError(
        'device_busy',
        `Encoder is in use by another program. Close THY_ReaderSoft and try again. (Msg=${openResult.Msg})`
      )
    }

    const info = await this.request('GetDeviceSystemInfo', [ANY_ANTENNA])
    const workMode = await this.ensureAnswerMode()
    const rfPower = await this.readParam(PARAM_RF_POWER)
    const decoded = this.parseDeviceInfo(info)

    this.deviceInfo = {
      serial: decoded.serial,
      firmware: decoded.firmware,
      hardware: decoded.hardware,
      workMode,
      rfPower,
    }

    return this.deviceInfo
  }

  async disconnect(): Promise<void> {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        await this.request('CloseDevice', [ANY_ANTENNA])
      } catch {
        // Closing is best-effort: the socket is going away regardless.
      }
    }
    this.teardown('disconnected')
  }

  async readTid(): Promise<string> {
    const frame = await this.request('ReadCardG2', [
      ANY_ANTENNA,
      DEFAULT_ACCESS_PASSWORD,
      MEMORY_BANK_TID,
      TID_PTR,
      TID_WORDS,
    ])
    assertOk(
      frame,
      'ReadCardG2',
      'no_tag',
      'No tag detected on the encoder. Place a tag and retry.'
    )
    const tid = hexFromFrame(frame, TID_WORDS * 4)
    if (!tid)
      throw new EncoderError(
        'no_tag',
        'No tag detected on the encoder. Place a tag and retry.'
      )
    return tid
  }

  async readPc(): Promise<string> {
    const frame = await this.request('ReadCardG2', [
      ANY_ANTENNA,
      DEFAULT_ACCESS_PASSWORD,
      MEMORY_BANK_EPC,
      EPC_PTR_PC_WORD,
      1,
    ])
    assertOk(frame, 'ReadCardG2', 'read_failed', 'Could not read the tag PC word.')
    const pc = hexFromFrame(frame, 4)
    if (!pc) throw new EncoderError('read_failed', 'Could not read the tag PC word.')
    return pc
  }

  async readEpc(words: number): Promise<string> {
    const frame = await this.request('ReadCardG2', [
      ANY_ANTENNA,
      DEFAULT_ACCESS_PASSWORD,
      MEMORY_BANK_EPC,
      EPC_PTR_EPC_DATA,
      words,
    ])
    assertOk(frame, 'ReadCardG2', 'read_failed', 'Could not read the tag EPC.')
    const epc = hexFromFrame(frame, words * 4)
    if (!epc) throw new EncoderError('read_failed', 'Could not read the tag EPC.')
    return epc
  }

  async writeEpc(epcHex: string, words: number): Promise<void> {
    const epc = epcHex.trim().toUpperCase()
    if (epc.length !== words * 4) {
      throw new EncoderError(
        'write_failed',
        `EPC length ${epc.length} does not match ${words} words (${words * 4} hex characters).`
      )
    }

    const frame = await this.request('WriteEPCG2', [
      ANY_ANTENNA,
      DEFAULT_ACCESS_PASSWORD,
      epc,
      words,
    ])

    assertOk(frame, 'WriteEPCG2', 'write_failed', 'The encoder rejected the EPC write.')
  }

  // -------------------------------------------------------------------------
  // Transport
  // -------------------------------------------------------------------------

  private setStatus(next: EncoderConnectionStatus): void {
    if (this.status === next) return
    this.status = next
    for (const listener of this.listeners) listener(next)
  }

  private pushFrameLog(entry: Omit<EncoderFrameLogEntry, 'at'>): void {
    const line: EncoderFrameLogEntry = { ...entry, at: new Date().toISOString() }
    this.frameLog.push(line)
    if (this.frameLog.length > FRAME_LOG_LIMIT) this.frameLog.shift()
    console.debug(`[thy-encoder] ${entry.direction}`, entry.ops ?? '', entry.raw)
  }

  private openSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.setStatus('connecting')

      let socket: WebSocket
      try {
        socket = new WebSocket(BRIDGE_URL)
      } catch {
        this.setStatus('disconnected')
        reject(
          new EncoderError(
            'bridge_unreachable',
            'RFID bridge not running. Start WebSocketServer.exe on this PC.'
          )
        )
        return
      }

      const timer = setTimeout(() => {
        socket.close()
        this.setStatus('disconnected')
        reject(
          new EncoderError(
            'bridge_unreachable',
            'RFID bridge not running. Start WebSocketServer.exe on this PC.'
          )
        )
      }, CONNECT_TIMEOUT_MS)

      socket.onopen = () => {
        clearTimeout(timer)
        this.socket = socket
        this.consecutiveTimeouts = 0
        this.setStatus('connected')
        resolve()
      }

      socket.onerror = () => {
        clearTimeout(timer)
        if (this.status !== 'connected') {
          this.setStatus('disconnected')
          reject(
            new EncoderError(
              'bridge_unreachable',
              'RFID bridge not running. Start WebSocketServer.exe on this PC.'
            )
          )
        }
      }

      socket.onclose = () => {
        clearTimeout(timer)
        this.teardown('disconnected')
      }

      socket.onmessage = (event) => this.handleMessage(event.data)
    })
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') return

    this.pushFrameLog({ direction: 'recv', raw })

    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      // "Welcome to RFID Server" and any other non-JSON chatter.
      return
    }

    if (!isBridgeFrame(parsed)) return

    // Msg 2 arrives unsolicited; Msg 250/251 are connection notifications.
    if (parsed.Msg === 2) return
    if (parsed.Msg === 250 || parsed.Msg === 251) {
      this.setStatus(parsed.Msg === 251 ? 'connected' : 'disconnected')
      return
    }

    const pending = this.pending
    if (!pending) return
    this.pending = null
    clearTimeout(pending.timer)
    this.consecutiveTimeouts = 0
    pending.resolve(parsed)
  }

  /** Serialises every request: one in flight, awaited before the next. */
  private request(ops: string, values: unknown[]): Promise<BridgeFrame> {
    const run = () => this.sendOne(ops, values)
    const next = this.queue.then(run, run)
    this.queue = next.then(
      () => undefined,
      () => undefined
    )
    return next
  }

  private sendOne(ops: string, values: unknown[]): Promise<BridgeFrame> {
    return new Promise<BridgeFrame>((resolve, reject) => {
      const socket = this.socket
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        reject(
          new EncoderError(
            'bridge_unreachable',
            'RFID bridge not running. Start WebSocketServer.exe on this PC.'
          )
        )
        return
      }

      const timer = setTimeout(() => {
        // Drain: the slot is cleared so a late reply is discarded by
        // handleMessage instead of being handed to the next request.
        this.pending = null
        this.consecutiveTimeouts += 1
        reject(
          this.consecutiveTimeouts >= 3
            ? new EncoderError(
                'bridge_duplicate',
                'Multiple copies of WebSocketServer.exe may be running. Close all and start one.'
              )
            : new EncoderError('protocol', `Encoder did not answer ${ops} in time.`)
        )
      }, REQUEST_TIMEOUT_MS)

      this.pending = { resolve, reject, timer }

      const payload = JSON.stringify({ Ops: ops, Val: values })
      this.pushFrameLog({ direction: 'sent', ops, raw: payload })

      try {
        socket.send(payload)
      } catch {
        clearTimeout(timer)
        this.pending = null
        reject(new EncoderError('protocol', `Failed to send ${ops} to the RFID bridge.`))
      }
    })
  }

  private teardown(status: EncoderConnectionStatus): void {
    if (this.pending) {
      clearTimeout(this.pending.timer)
      this.pending.reject(
        new EncoderError('bridge_unreachable', 'Connection to the RFID bridge was lost.')
      )
      this.pending = null
    }
    if (this.socket) {
      this.socket.onopen = null
      this.socket.onclose = null
      this.socket.onerror = null
      this.socket.onmessage = null
      if (this.socket.readyState === WebSocket.OPEN) this.socket.close()
      this.socket = null
    }
    this.deviceInfo = null
    this.setStatus(status)
  }

  // -------------------------------------------------------------------------
  // Device parameters
  // -------------------------------------------------------------------------

  private async readParam(param: number): Promise<number | null> {
    try {
      const frame = await this.request('ReadDeviceOneParam', [ANY_ANTENNA, param])
      if (frame.Msg !== MSG_OK) return null
      return numberFromFrame(frame)
    } catch {
      return null
    }
  }

  /**
   * Always set Answer mode on connect. Documented and known to work. Never
   * throw — a misread must not block encoding on a correctly configured device.
   */
  private async ensureAnswerMode(): Promise<number | null> {
    try {
      await this.request('SetDeviceOneParam', [
        ANY_ANTENNA,
        PARAM_WORK_MODE,
        WORK_MODE_ANSWER,
      ])
    } catch {
      // Setting is best-effort; the read-back below surfaces the result.
    }
    return this.readParam(PARAM_WORK_MODE)
  }

  private parseDeviceInfo(frame: BridgeFrame): {
    serial: string | null
    firmware: string | null
    hardware: string | null
  } {
    const value = frame.Val
    if (typeof value === 'string') return decodeDeviceInfoString(value)
    if (Array.isArray(value)) {
      const first = value.find((entry): entry is string => typeof entry === 'string')
      if (first) return decodeDeviceInfoString(first)
    }
    return { serial: null, firmware: null, hardware: null }
  }
}
